import React, { useEffect, useRef } from 'react';
import AceEditor, { IAceOptions } from 'react-ace';
import { Ace } from 'ace-builds';
import 'ace-builds/src-noconflict/mode-text';
import 'ace-builds/src-noconflict/mode-pgsql';
import 'ace-builds/src-noconflict/mode-json';
import './Editor.scss';
import 'ace-builds/src-noconflict/ext-searchbox';
import { InternalApiScenarioLanguage } from '../../api-internal';
import 'ace-builds/src-noconflict/theme-tomorrow';
import 'ace-builds/src-noconflict/ext-language_tools';

export interface EditorProps {
  disabled: boolean;
  value: string;
  onChange: (newValue: string) => void;
  type?: InternalApiScenarioLanguage;
  syntaxHighlight?: boolean;
  enableLiveAutocompletion?: boolean;
  className?: string;
  showLineNumbers?: boolean;
  highlightingMode?: Ace.SyntaxMode;
  completers?: Ace.Completer[];
  withBorder?: boolean;
  isInvalid?: boolean;
  minLines?: number;
  placeholder?: string;
  options?: Partial<IAceOptions>;
  maxLines?: number;
}

// mock in setupTests.js
const Editor = ({
  disabled,
  value,
  onChange,
  type,
  className,
  syntaxHighlight = true,
  enableLiveAutocompletion = false,
  showLineNumbers = true,
  highlightingMode,
  completers,
  withBorder = false,
  isInvalid = false,
  minLines = 10,
  maxLines = disabled ? value.split('\n').length + 2 : 30,
  options,
}: EditorProps) => {
  const mode = syntaxHighlight && !disabled ? (type === InternalApiScenarioLanguage.SQL ? 'pgsql' : 'json') : 'text';

  const aceEditorRef = useRef(null);
  const editor = (aceEditorRef?.current as any)?.editor as Ace.Editor | null;

  useEffect(() => {
    if (editor != null && !disabled && enableLiveAutocompletion) {
      if (highlightingMode) {
        editor.session.setMode(highlightingMode);
      }

      if (completers?.length) {
        editor.completers = completers;
      }
    }
  }, [editor, highlightingMode, completers]);

  useEffect(() => {
    if (editor != null && !disabled && enableLiveAutocompletion) {
      editor.execCommand('startAutocomplete', editor);
    }
  }, [editor, value]);

  return (
    <AceEditor
      ref={aceEditorRef}
      mode={mode}
      theme='tomorrow'
      name='code-editor'
      onChange={onChange}
      showPrintMargin={false}
      showGutter
      highlightActiveLine={false}
      value={value}
      readOnly={disabled}
      className={`code-editor ${withBorder ? 'with-border' : ''} ${isInvalid ? 'is-invalid' : ''} ${className || ''} ${disabled ? 'disabled' : ''}`}
      width=''
      onLoad={(editor) => {
        editor.renderer.setPadding(10);
        editor.renderer.setScrollMargin(10, 10, 10, 10);
      }}
      editorProps={{ autoScrollEditorIntoView: true }}
      minLines={minLines}
      maxLines={maxLines}
      wrapEnabled
      setOptions={{
        showLineNumbers: showLineNumbers,
        tabSize: 2,
        ...options,
      }}
      enableBasicAutocompletion={enableLiveAutocompletion}
      enableLiveAutocompletion={enableLiveAutocompletion}
    />
  );
};

export default Editor;
